import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Cookie from 'js-cookie';

// third-party
import 'firebase/compat/auth';
import {
  User,
  UserCredential,
  UserInfo,
  getAuth,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithCustomToken as firebaseSignInWithCustomToken,
  signInWithRedirect,
  OAuthProvider,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  initializeAuth,
  connectAuthEmulator,
} from 'firebase/auth';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  getToken,
  AppCheck,
} from '@firebase/app-check';
import { useLocalStorage } from '@uidotdev/usehooks';
import Loader from '@assetron/web-common/components/common/Loader';
import {
  AUTH_APP_RETURN_URL_QUERY,
  IS_USER_LOGGED_IN,
} from '@assetron/web-common/constants/LocalStorageConstants';
import { getApps, getApp, initializeApp } from 'firebase/app';
import useConfig from '../hooks/useConfig';
import Config from '../config';
import { SIGN_OUT_QUERY_PARAM } from '../constants/constant';
import { deleteCookie } from '../utils/setSameSiteCookieWithoutExpiry';
import {
  effectForDecision,
  isProductionEnv,
  readDebugTokenGlobal,
  runAppCheckEffect,
  selectAppCheckProvider,
} from './appCheckConfig';

export interface AuthWebContextProps {
  auth: ReturnType<typeof getAuth>;
  userInformation: User | null;
  isUserAuthenticated: boolean;
  signInWithEmailPassword: (
    email: string,
    password: string,
  ) => Promise<UserCredential>;
  signInWithCustomToken: (customToken: string) => Promise<UserCredential>;
  authWithGoogle: () => Promise<void>;
  authWithMicrosoft: () => Promise<void>;
  authWithApple: () => Promise<void>;
  getAppCheckToken: () => Promise<string | null>;
}

const firebaseConfig = {
  apiKey: Config.FIREBASE_API_KEY,
  authDomain: Config.FIREBASE_AUTH_DOMAIN,
  projectId: Config.FIREBASE_PROJECT_ID,
  storageBucket: Config.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: Config.FIREBASE_MESSAGING_SENDER_ID,
  appId: Config.FIREBASE_APP_ID,
  measurementId: Config.FIREBASE_MEASUREMENT_ID,
};

const app = getApps().length
  ? getApp('assetron-dev')
  : initializeApp(firebaseConfig, 'assetron-dev');

export const auth = initializeAuth(app, {
  persistence: [indexedDBLocalPersistence, browserLocalPersistence],
});

// ── Connect to Firebase Auth Emulator when configured ─────────────────────
const useFirebaseEmulator = Config.USE_FIREBASE_EMULATOR === 'true';
const hasHostedAutomationQueryFlag = (): boolean => {
  if (globalThis.window === undefined) {
    return false;
  }

  const hostname = globalThis.window.location.hostname.toLowerCase();
  if (hostname !== 'assetron.dev' && !hostname.endsWith('.assetron.dev')) {
    return false;
  }

  const value = new URLSearchParams(globalThis.window.location.search).get(
    'testAutomation',
  );
  if (!value) {
    return false;
  }

  const normalized = value.trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
};

const disableAppCheckForCi =
  Config.DISABLE_APP_CHECK_CI === 'true' || hasHostedAutomationQueryFlag();
const firebaseAuthEmulatorUrl =
  Config.FIREBASE_AUTH_EMULATOR_URL ||
  `http://${Config.FIREBASE_AUTH_EMULATOR_HOST || 'localhost:9099'}`;
if (useFirebaseEmulator) {
  connectAuthEmulator(auth, firebaseAuthEmulatorUrl, {
    disableWarnings: true,
  });
}

// Lazy AppCheck.
// Initialized with the production ReCaptcha Enterprise provider. Playwright
// tests inject an allowlisted debug token (see playwright/firebaseAppCheck.ts)
// via an init script that runs before this module, which switches the SDK to
// the Firebase debug provider automatically — no branching needed here.
// See https://firebase.google.com/docs/app-check/web/debug-provider.
//
// Fail-fast note: the BE enforces App Check on every request with no opt-out.
// If getFirebaseAppCheck is called without a viable provider (no ReCaptcha
// site key, no debug-token init script) in a non-production build, we throw
// with a clear remediation instead of silently returning null and producing
// 401s across the app. Only explicit CI/test automation escape hatches are
// allowed here. Running Firebase Auth against the local emulator does NOT
// disable App Check for browser API requests.
let cachedAppCheck: AppCheck | null = null;

export const getFirebaseAppCheck = (): AppCheck | null => {
  const isProduction = isProductionEnv(
    typeof process === 'undefined' ? undefined : process.env,
  );
  return runAppCheckEffect<AppCheck>(
    effectForDecision(
      selectAppCheckProvider({
        recaptchaSiteKey: Config.RECAPTCHA_SITE_KEY,
        debugTokenGlobal: readDebugTokenGlobal(
          typeof globalThis === 'undefined' ? undefined : (globalThis as never),
        ),
        isProduction,
        escapeHatchActive: disableAppCheckForCi,
      }),
      { isProduction },
    ),
    {
      activate: (siteKey) => {
        cachedAppCheck ??= initializeAppCheck(app, {
          provider: new ReCaptchaEnterpriseProvider(siteKey),
          isTokenAutoRefreshEnabled: true,
        });
        return cachedAppCheck;
      },
      // eslint-disable-next-line no-console
      warn: (m) => console.warn(m),
      // eslint-disable-next-line no-console
      error: (m) => console.error(m),
    },
  );
};

// Utility function to get the App Check token (handles null gracefully)
export const getAppCheckToken = (): Promise<string | null> => {
  const appCheck = getFirebaseAppCheck();
  if (!appCheck) return Promise.resolve(null);
  return getToken(appCheck, false)
    .then(({ token }) => token)
    .catch((err: unknown) => {
      // eslint-disable-next-line no-console, @typescript-eslint/no-explicit-any
      console.warn('AppCheck failed:', (err as any)?.code || err);
      return null;
    });
};

// ==============================|| FIREBASE CONTEXT & PROVIDER ||============================== //

const AuthWebContext = createContext<AuthWebContextProps | null>(null);

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

const toUrl = (value: string | null | undefined): URL | null => {
  if (!value) {
    return null;
  }

  try {
    return new URL(value);
  } catch {
    return null;
  }
};

const isLocalOrigin = (origin: string): boolean => {
  const parsed = toUrl(origin);
  if (!parsed) {
    return false;
  }

  return LOCAL_HOSTS.has(parsed.hostname);
};

const resolveSafeReturnUrl = (
  configuredAppUrl: string | undefined,
  storedReturnUrl: string,
): string => {
  const currentOrigin = globalThis.location.origin;
  const currentOriginIsLocal = isLocalOrigin(currentOrigin);
  const configured = toUrl(configuredAppUrl);
  const stored = toUrl(storedReturnUrl);

  // On localhost, never allow redirects to non-local origins from stale storage.
  if (currentOriginIsLocal) {
    if (stored && isLocalOrigin(stored.origin)) {
      return stored.toString();
    }

    if (configured && isLocalOrigin(configured.origin)) {
      return configured.toString();
    }

    return currentOrigin;
  }

  if (stored) {
    return stored.toString();
  }

  if (configured) {
    return configured.toString();
  }

  return currentOrigin;
};

export function AuthWebProvider({
  children,
  shouldNavigate = true,
}: Readonly<{
  children: React.ReactElement;
  shouldNavigate?: boolean;
}>) {
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [userInformation, setUserInformation] = useState<
    User | UserInfo | null
  >(auth.currentUser);

  const { APP_URL } = useConfig();
  const [appURL] = useLocalStorage(AUTH_APP_RETURN_URL_QUERY, APP_URL);

  useEffect(() => {
    const urlParams = new URLSearchParams(globalThis.location.search);
    const isSignOutRequested =
      urlParams.get(SIGN_OUT_QUERY_PARAM)?.toLowerCase() === 'true';
    if (isSignOutRequested) {
      return;
    }

    if (
      Cookie.get(IS_USER_LOGGED_IN) === 'true' &&
      !!auth.currentUser &&
      shouldNavigate
    ) {
      const finalUrl = resolveSafeReturnUrl(APP_URL, appURL);
      const { search } = globalThis.location;
      if (search) {
        globalThis.location.href = `${finalUrl}${search}`;
      } else {
        globalThis.location.href = finalUrl;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [APP_URL, appURL, shouldNavigate, auth.currentUser]);

  useEffect(() => {
    const handleSignOut = async () => {
      const urlParams = new URLSearchParams(globalThis.location.search);
      if (urlParams.get(SIGN_OUT_QUERY_PARAM) === 'true') {
        await auth.signOut();
        setUserInformation(null);
        deleteCookie(IS_USER_LOGGED_IN);
        urlParams.delete(SIGN_OUT_QUERY_PARAM);

        // Preserve other query parameters in the URL
        const remainingParams = urlParams.toString();
        const newUrl = remainingParams
          ? `${globalThis.location.pathname}?${remainingParams}`
          : globalThis.location.pathname;
        globalThis.history.replaceState({}, document.title, newUrl);
        setIsAuthInitialized(true);
      } else {
        auth.onAuthStateChanged(() => {
          setIsAuthInitialized(true);
        });
      }
    };

    handleSignOut();
  }, [setUserInformation]);

  const isUserAuthenticated = !!userInformation;

  const signInWithEmailPassword = useCallback(
    (email: string, password: string) => {
      return signInWithEmailAndPassword(auth, email, password);
    },
    [],
  );

  const signInWithCustomToken = useCallback((customToken: string) => {
    return firebaseSignInWithCustomToken(auth, customToken);
  }, []);

  const authWithGoogle = useCallback(async () => {
    const googleProvider = new GoogleAuthProvider().setCustomParameters({
      prompt: 'select_account',
    });
    await signInWithRedirect(
      auth,
      googleProvider,
      browserPopupRedirectResolver,
    );
  }, []);

  const authWithMicrosoft = useCallback(async () => {
    const microsoftProvider: OAuthProvider = new OAuthProvider('microsoft.com');

    microsoftProvider.setCustomParameters({
      prompt: 'select_account',
    });

    await signInWithRedirect(
      auth,
      microsoftProvider,
      browserPopupRedirectResolver,
    );
  }, []);

  const authWithApple = useCallback(async () => {
    const appleProvider = new OAuthProvider('apple.com');

    appleProvider.addScope('email');
    appleProvider.addScope('name');

    appleProvider.setCustomParameters({
      prompt: 'select_account',
      response_mode: 'form_post',
    });

    await signInWithRedirect(auth, appleProvider, browserPopupRedirectResolver);
  }, []);

  const AuthWebContextValue = useMemo(() => {
    return {
      auth,
      userInformation,
      isUserAuthenticated,
      signInWithEmailPassword,
      signInWithCustomToken,
      authWithGoogle,
      authWithMicrosoft,
      authWithApple,
      getAppCheckToken,
    };
  }, [
    userInformation,
    isUserAuthenticated,
    signInWithEmailPassword,
    signInWithCustomToken,
    authWithGoogle,
    authWithMicrosoft,
    authWithApple,
  ]);

  if (!isAuthInitialized) return <Loader />;

  return (
    <AuthWebContext.Provider value={AuthWebContextValue}>
      {children}
    </AuthWebContext.Provider>
  );
}

export default AuthWebContext;
